import { IcFlightPlan } from '@assets/icons/40x40';
import { HStack, Icon, Tabs, Text, VStack } from '@chakra-ui/react';
import FlightPlanDetailsWithValidation from '@components/map/popup/infos/flightplans/FlightPlanDetailsWithValidation';
import FlightPlanDrones from '@components/map/popup/infos/flightplans/FlightPlanDrones';
import FlightPlanInstructions from '@components/map/popup/infos/flightplans/FlightPlanInstructions';
import FlightPlanPilots from '@components/map/popup/infos/flightplans/FlightPlanPilots';
import { DraggablePanel } from '@components/ui/draggable';
import { FormattedMessage, useIntl } from 'react-intl';

import { ActivationAndFlightPlanInfoData, PopupPositionData } from '@/types/popup.types';

export default function ActivationAndFlightPlanInfo({
  mapConstraintsRef,
  featureValue,
  top,
  left,
  onClose,
}: Readonly<ActivationAndFlightPlanInfoData & PopupPositionData>) {
  const { formatMessage } = useIntl();
  const { activation, flightPlan } = featureValue;
  const missionId = flightPlan.externalId ?? flightPlan.code;

  return (
    <DraggablePanel.Root constraintsRef={mapConstraintsRef} key={flightPlan.code} top={top} left={left} offsetY={-100}>
      <DraggablePanel.Header onClose={onClose}>
        <FormattedMessage id="flightPlanInfos.title" />
      </DraggablePanel.Header>
      <DraggablePanel.Body width={{ base: '416px', '4k': '612px' }} minHeight={610}>
        <VStack gap={0.25} width="100%" height="100%" backgroundColor="neutral.900">
          <HStack
            width="100%"
            height={{ base: '80px', '4k': '120px' }}
            gap={2}
            backgroundColor="neutral.700"
            justifyContent="flex-start"
            paddingLeft={3}
          >
            <Icon as={IcFlightPlan} boxSize={{ base: '32px', '4k': '40px' }} color="neutral.300" />
            <VStack gap={0.5} alignItems="start">
              <Text padding={0.5} backgroundColor="neutral.black">
                {flightPlan.name}
              </Text>
            </VStack>
          </HStack>
          <Tabs.Root maxHeight="712px" size="screenStd" variant="neutral" gap={0.25} lazyMount defaultValue="details">
            <Tabs.List>
              <Tabs.Trigger value="details">
                <FormattedMessage id="flightPlanInfos.tab.details.title" />
              </Tabs.Trigger>
              <Tabs.Trigger value="instructions">
                <FormattedMessage id="flightPlanInfos.tab.instructions.title" />
              </Tabs.Trigger>
              <Tabs.Trigger value="drones">
                <FormattedMessage id="flightPlanInfos.tab.drones.title" />
              </Tabs.Trigger>
              <Tabs.Trigger value="pilots">
                <FormattedMessage id="flightPlanInfos.tab.pilots.title" />
              </Tabs.Trigger>
            </Tabs.List>
            <Tabs.ContentGroup>
              <Tabs.Content value="details" className="cockpit-scrollbar-900">
                <FlightPlanDetailsWithValidation
                  maxAltitude={flightPlan.altitudeMax}
                  mission={missionId}
                  provider={flightPlan.provider}
                  missionStartTime={activation.startDate}
                  missionEndTime={activation.endDate}
                  information={[]}
                  activationAndFlightPlan={featureValue}
                />
              </Tabs.Content>
              <Tabs.Content value="instructions">
                <FlightPlanInstructions instruction={formatMessage({ id: 'flightPlanInfos.tab.instructions.empty' })} />
              </Tabs.Content>
              <Tabs.Content value="drones">
                <FlightPlanDrones drones={flightPlan.drones} />
              </Tabs.Content>
              <Tabs.Content value="pilots">
                <FlightPlanPilots pilots={flightPlan.drones.flatMap((drone) => drone.pilots)} />
              </Tabs.Content>
            </Tabs.ContentGroup>
          </Tabs.Root>
        </VStack>
      </DraggablePanel.Body>
    </DraggablePanel.Root>
  );
}
